/**
 * Chat Domain Tools
 *
 * Thunk-pattern implementations for chat-level operations that mirror
 * the `Chat` helpers in `whatsapp-web.js` (`src/structures/Chat.js`).
 *
 * Every tool shares the same dual-mode flow, captured once in `createChatTool`:
 *  - Extension mode: direct call into the page context via `WhatsappToolsClientActions`.
 *  - Server mode: request is routed through the backend -> MQTT -> engine via `triggerRequesthWhatsappTool`.
 */

import { isWhatsappExtensionContext } from "@common-services/detect-context";
import { type WireResponse } from "@common/services/post-message-to-whatsapp-functions";
import { unwrapEngineResult } from "@bt-common/interfaces/whatsapp-tools-commands.interface";
import { WhatsappToolsClientActions } from "@common/tools/whatsapp/wa-tools-client-actions";
import {
    triggerRequesthWhatsappTool,
    type SendSeenParams,
    type ClearMessagesParams,
    type DeleteChatParams,
    type ArchiveChatParams,
    type UnarchiveChatParams,
    type PinChatParams,
    type UnpinChatParams,
    type MuteChatParams,
    type UnmuteChatParams,
    type MarkChatUnreadParams,
    type SendStateTypingParams,
    type SendStateRecordingParams,
    type ClearStateParams,
    type GetChatLabelsParams,
    type ChangeLabelsParams,
    type GetPinnedMessagesParams,
    type AddOrEditCustomerNoteParams,
} from "@common/tools/whatsapp/wa-tools-server-actions";
import { validateChatIdForTool } from "@common/tools/whatsapp/wa-chat-id-validation";
import { WA_TOOL_COMMANDS, type WaToolResponse } from "../../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolArgs, WaToolCommand, WaToolResult } from "../../../../bt-common/interfaces/whatsapp-tool-contracts.interface";

// invariant: validateChatIdForTool only returns null when chatId is a non-empty string
const assertChatId = (chatId: string | undefined): string => {
    if (!chatId) throw new Error('chatId invariant violated after validateChatIdForTool');
    return chatId;
};

// Thunk signature shared by every chat tool — the same per-command contract
// shape the hand-written declarations spelled out.
type ChatTool<C extends WaToolCommand> = (
    params: WaToolArgs<C>,
) => Promise<WaToolResponse<WaToolResult<C>>>;

/**
 * Factory capturing the dual-mode flow shared by every chat tool below:
 *  validate chatId → extension mode: `clientAction` + wire-error unwrap →
 *  server mode: `triggerRequesthWhatsappTool(command, serverParams)`.
 *
 * `buildParams` is the per-tool param pre-processing hook (mute's
 * `unmuteDate` passthrough, labels/note munging); it defaults to the plain
 * `{ chatId }` payload. The same built params feed both the client action
 * and the server request, exactly as the hand-written thunks did.
 */
const createChatTool = <
    TArgs extends { chatId?: string } = { chatId?: string },
    TParams extends { chatId: string } = { chatId: string },
    TResult = unknown,
>(
    command: string,
    clientAction: (serverParams: TParams) => Promise<WireResponse<TResult> | undefined>,
    buildParams: (chatId: string, params: TArgs) => TParams = (chatId) => ({ chatId } as TParams),
) => async (params: TArgs): Promise<WaToolResponse<TResult>> => {
    // Local-only chatId check (presence/format). The on-WhatsApp existence
    // round-trip is intentionally NOT requested here — these tools act on an
    // existing chat and the inner engine op fails for a missing one, so a
    // waValidatePhoneNumberOrChatIdTool pre-flight would be pure overhead.
    const chatIdError = await validateChatIdForTool(params.chatId);
    if (chatIdError) return chatIdError;
    const chatId = assertChatId(params.chatId);
    const serverParams = buildParams(chatId, params);

    if (isWhatsappExtensionContext()) {
        const result = await clientAction(serverParams);
        return unwrapEngineResult(result);
    } else {
        return await triggerRequesthWhatsappTool<TParams, TResult>(command, serverParams);
    }
};


// Shares the createChatTool flow like every other chat mutation: the page
// handler emits the `{result}` envelope (the underlying WAWeb sendSeen boolean),
// so callers can tell whether the chat was actually marked as seen. The client
// action keeps its shorter 2s timeout internally.
export const waSendSeenTool: ChatTool<typeof WA_TOOL_COMMANDS.waSendSeenTool> =
    createChatTool(WA_TOOL_COMMANDS.waSendSeenTool, (p: SendSeenParams) => WhatsappToolsClientActions.waSendSeenTool(p.chatId));

export const waClearMessagesTool: ChatTool<typeof WA_TOOL_COMMANDS.waClearMessagesTool> =
    createChatTool(WA_TOOL_COMMANDS.waClearMessagesTool, (p: ClearMessagesParams) => WhatsappToolsClientActions.waClearMessagesTool(p.chatId));

export const waDeleteChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waDeleteChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waDeleteChatTool, (p: DeleteChatParams) => WhatsappToolsClientActions.waDeleteChatTool(p.chatId));

export const waArchiveChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waArchiveChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waArchiveChatTool, (p: ArchiveChatParams) => WhatsappToolsClientActions.waArchiveChatTool(p.chatId));

export const waUnarchiveChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waUnarchiveChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waUnarchiveChatTool, (p: UnarchiveChatParams) => WhatsappToolsClientActions.waUnarchiveChatTool(p.chatId));

export const waPinChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waPinChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waPinChatTool, (p: PinChatParams) => WhatsappToolsClientActions.waPinChatTool(p.chatId));

export const waUnpinChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waUnpinChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waUnpinChatTool, (p: UnpinChatParams) => WhatsappToolsClientActions.waUnpinChatTool(p.chatId));

export const waMuteChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waMuteChatTool> = createChatTool(
    WA_TOOL_COMMANDS.waMuteChatTool,
    (p: MuteChatParams) => WhatsappToolsClientActions.waMuteChatTool(p.chatId, p.unmuteDate),
    // Contract: `unmuteDate?: string | number | null` (already serialised);
    // the Date-instance branch is gone (no ambiguous shapes — callers
    // serialise upstream).
    (chatId, params: WaToolArgs<typeof WA_TOOL_COMMANDS.waMuteChatTool>): MuteChatParams => ({ chatId, unmuteDate: params.unmuteDate }),
);

export const waUnmuteChatTool: ChatTool<typeof WA_TOOL_COMMANDS.waUnmuteChatTool> =
    createChatTool(WA_TOOL_COMMANDS.waUnmuteChatTool, (p: UnmuteChatParams) => WhatsappToolsClientActions.waUnmuteChatTool(p.chatId));

export const waMarkChatUnreadTool: ChatTool<typeof WA_TOOL_COMMANDS.waMarkChatUnreadTool> =
    createChatTool(WA_TOOL_COMMANDS.waMarkChatUnreadTool, (p: MarkChatUnreadParams) => WhatsappToolsClientActions.waMarkChatUnreadTool(p.chatId));

export const waSendStateTypingTool: ChatTool<typeof WA_TOOL_COMMANDS.waSendStateTypingTool> =
    createChatTool(WA_TOOL_COMMANDS.waSendStateTypingTool, (p: SendStateTypingParams) => WhatsappToolsClientActions.waSendStateTypingTool(p.chatId));

export const waSendStateRecordingTool: ChatTool<typeof WA_TOOL_COMMANDS.waSendStateRecordingTool> =
    createChatTool(WA_TOOL_COMMANDS.waSendStateRecordingTool, (p: SendStateRecordingParams) => WhatsappToolsClientActions.waSendStateRecordingTool(p.chatId));

export const waClearStateTool: ChatTool<typeof WA_TOOL_COMMANDS.waClearStateTool> =
    createChatTool(WA_TOOL_COMMANDS.waClearStateTool, (p: ClearStateParams) => WhatsappToolsClientActions.waClearStateTool(p.chatId));

export const waGetChatLabelsTool: ChatTool<typeof WA_TOOL_COMMANDS.waGetChatLabelsTool> =
    createChatTool(WA_TOOL_COMMANDS.waGetChatLabelsTool, (p: GetChatLabelsParams) => WhatsappToolsClientActions.waGetChatLabelsTool(p.chatId));

export const waChangeLabelsTool: ChatTool<typeof WA_TOOL_COMMANDS.waChangeLabelsTool> = createChatTool(
    WA_TOOL_COMMANDS.waChangeLabelsTool,
    (p: ChangeLabelsParams) => WhatsappToolsClientActions.waChangeLabelsTool(p.chatId, p.labelIds),
    (chatId, params: WaToolArgs<typeof WA_TOOL_COMMANDS.waChangeLabelsTool>): ChangeLabelsParams =>
        ({ chatId, labelIds: Array.isArray(params.labelIds) ? params.labelIds : [] }),
);

/** The account's labels (Business accounts) — account-wide, no chatId. */
export async function waGetLabelsTool(): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetLabelsTool>>> {
    if (isWhatsappExtensionContext()) {
        return unwrapEngineResult(await WhatsappToolsClientActions.waGetLabelsTool());
    }
    return triggerRequesthWhatsappTool<Record<string, never>, WaToolResult<typeof WA_TOOL_COMMANDS.waGetLabelsTool>>(
        WA_TOOL_COMMANDS.waGetLabelsTool, {},
    );
}

export const waGetPinnedMessagesTool: ChatTool<typeof WA_TOOL_COMMANDS.waGetPinnedMessagesTool> =
    createChatTool(WA_TOOL_COMMANDS.waGetPinnedMessagesTool, (p: GetPinnedMessagesParams) => WhatsappToolsClientActions.waGetPinnedMessagesTool(p.chatId));


export const waAddOrEditCustomerNoteTool: ChatTool<typeof WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool> = createChatTool(
    WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool,
    (p: AddOrEditCustomerNoteParams) => WhatsappToolsClientActions.waAddOrEditCustomerNoteTool(p.chatId, p.note),
    (chatId, params: WaToolArgs<typeof WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool>): AddOrEditCustomerNoteParams =>
        ({ chatId, note: String(params.note ?? '') }),
);
